import { useState } from 'react'
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    Field,
    Input,
    List,
    Portal,
    RadioGroup,
    Stack,
    Text,
} from '@chakra-ui/react'
import { deleteAuthUser } from '../../services/userAdmin'
import { confirmMatches, confirmTarget, deletionLines, videoTotals } from '../../utils/userDeletion'
import { accountLabel } from '../../utils/userDisplay'
import { useTranslation } from '../../i18n'

function Lines({ title, lines }) {
    const { t } = useTranslation()
    if (lines.length === 0) return null
    return (
        <Box>
            <Text fontWeight="600" color="var(--mc-text)" mb={1}>{title}</Text>
            <List.Root ps={5} gap={1} color="var(--mc-text)" fontSize="sm">
                {lines.map((l) => <List.Item key={l.key}>{t(`users.delete.lines.${l.key}`, l.vars)}</List.Item>)}
            </List.Root>
        </Box>
    )
}

/**
 * Confirm and run a removal. `kind` is 'access' (roles and permissions only)
 * or 'delete' (the account, with a choice about the videos). Calls `onDone`
 * with the mode once the server reports a complete run; a partial run stays
 * open, lists what failed, and offers to try again — every step is safe to
 * repeat, and the login is kept until everything else is gone.
 */
export function DeleteUserDialog({ user, kind, onClose, onDone }) {
    const { t } = useTranslation()
    const [withVideos, setWithVideos] = useState(false)
    const [typed, setTyped] = useState('')
    const [working, setWorking] = useState(false)
    const [failures, setFailures] = useState(null)
    const [error, setError] = useState(null)

    const mode = kind === 'access' ? 'access' : (withVideos ? 'everything' : 'account')
    const { removed, kept } = deletionLines(user, mode)
    const totals = videoTotals(user)
    const target = confirmTarget(user)
    const confirmed = confirmMatches(user, typed)

    const run = async () => {
        setWorking(true)
        setError(null)
        try {
            const result = await deleteAuthUser({ uid: user.uid, mode })
            if (result.completed) onDone(mode)
            else setFailures(result.failures)
        } catch (err) {
            setError(err.message)
        } finally {
            setWorking(false)
        }
    }

    const confirmLabel = {
        access: t('users.delete.confirmAccess'),
        account: t('users.delete.confirmAccount'),
        everything: t('users.delete.confirmEverything'),
    }[mode]

    return (
        <Dialog.Root
            open
            role="alertdialog"
            size="lg"
            onOpenChange={(e) => { if (!e.open && !working) onClose() }}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>
                                {kind === 'access' ? t('users.delete.titleAccess') : t('users.delete.titleAccount')}
                            </Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                            <Stack gap={4}>
                                <Text color="var(--mc-text)" fontWeight="600">
                                    {accountLabel(user, t)}
                                    <Text as="span" fontWeight="400" color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace" fontSize="xs"> · {user.uid}</Text>
                                </Text>

                                {kind === 'delete' && totals.videos > 0 && (
                                    <RadioGroup.Root
                                        value={withVideos ? 'everything' : 'account'}
                                        onValueChange={(e) => setWithVideos(e.value === 'everything')}
                                        disabled={working}
                                    >
                                        <Stack gap={2}>
                                            <RadioGroup.Item value="account">
                                                <RadioGroup.ItemHiddenInput />
                                                <RadioGroup.ItemIndicator />
                                                <RadioGroup.ItemText>{t('users.delete.keepVideos')}</RadioGroup.ItemText>
                                            </RadioGroup.Item>
                                            <RadioGroup.Item value="everything">
                                                <RadioGroup.ItemHiddenInput />
                                                <RadioGroup.ItemIndicator />
                                                <RadioGroup.ItemText>{t('users.delete.alsoVideos', totals)}</RadioGroup.ItemText>
                                            </RadioGroup.Item>
                                        </Stack>
                                    </RadioGroup.Root>
                                )}

                                <Lines title={t('users.delete.willRemove')} lines={removed} />
                                <Lines title={t('users.delete.willKeep')} lines={kept} />

                                {mode !== 'access' && (
                                    <Text fontSize="sm" color="var(--mc-red)" fontWeight="600">{t('users.delete.irreversible')}</Text>
                                )}

                                {failures && (
                                    <Box role="alert" p={3} borderRadius="md" bg="var(--mc-red-bg)">
                                        <Text fontSize="sm" color="var(--mc-text)" mb={1}>{t('users.delete.partial')}</Text>
                                        <List.Root ps={5} fontSize="xs" fontFamily="'JetBrains Mono', monospace" color="var(--mc-text)">
                                            {failures.slice(0, 20).map((f) => <List.Item key={f}>{f}</List.Item>)}
                                        </List.Root>
                                    </Box>
                                )}
                                {error && (
                                    <Text role="alert" fontSize="sm" color="var(--mc-red)">{error}</Text>
                                )}

                                <Field.Root>
                                    <Field.Label>{t('users.delete.typeToConfirm', { target })}</Field.Label>
                                    <Input
                                        size="sm"
                                        value={typed}
                                        onChange={(e) => setTyped(e.target.value)}
                                        autoComplete="off"
                                        spellCheck={false}
                                        disabled={working}
                                    />
                                </Field.Root>
                            </Stack>
                        </Dialog.Body>
                        <Dialog.Footer>
                            <Button variant="outline" disabled={working} onClick={onClose}>{t('common.cancel')}</Button>
                            <Button
                                colorPalette="red"
                                disabled={!confirmed}
                                loading={working}
                                loadingText={t('users.delete.working')}
                                onClick={run}
                            >
                                {failures ? t('users.delete.tryAgain') : confirmLabel}
                            </Button>
                        </Dialog.Footer>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" disabled={working} />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    )
}
